import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { ArrowLeft, Timer, User, MapPin, Phone, Mail, Calendar, Building, FileText, Save, ArrowRight, CheckCircle, Clock, MessageSquare, X, Play, Pause, Copy, AlertTriangle, Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import EmptyState from '@/components/common/EmptyState';
import MultiSelect from '@/components/common/MultiSelect';
import GroupCompletionDialog from '@/components/common/GroupCompletionDialog';
import { openSendSMSModal } from 'hbox-sms';
import { TransferStatus } from '@/components/common/TransferStatus.tsx';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

import { PatientDetailsTimer } from '@/components/common/PatientDetailsTimer';
import {
  setPatientTimerAutoStart,
  silentPatientTimerCleanup,
  resumeTimersForNewPatient
} from '@/hooks/patient/usePatientDetailsTimer.ts';
import { usePatientDetailsTimer } from '@/hooks/patient/usePatientDetailsTimer.ts';
import { useGlobalTimelog } from '@/hooks/system/useGlobalTimelog.ts';
import CallHandler from '@/components/CallHandler';
import { Progress } from '@/components/ui/progress';
import { useHBoxCall } from '@/components/CallHandler/hooks/useHBoxCall';
import { usePhoneValidation } from '@/hooks/usePhoneValidation';

// Import our clean hooks
import { useESPatientData } from '@/hooks/es/useESPatientData.ts';
import { useESPatientOperations } from '@/hooks/es/useESPatientOperations.ts';
import AppointmentScheduler, { AppointmentData } from '@/components/es/AppointmentScheduler';
import { useAuth } from '@/contexts/AuthContext';
import { convertUTCToTimezone, getTimezoneAbbreviation } from '@/utils/timezoneUtils';
import { miscGraphqlService } from '@/services/api/misc/miscGraphqlService';
import { API_CONFIG } from '@/config/envConfig';
import { serviceFactory } from '@/services/core/serviceFactory';
import { DropdownDataService, type Clinic, type Provider, type Facility } from '@/services/features/DropdownDataService';
import { useProgramTypeStatuses } from '@/hooks/workflow/useProgramTypeStatuses';
import { useWorkflowStatusesList } from '@/hooks/workflow/useWorkflowStatusesList';
import { usePatientAppointments, PatientAppointment } from '@/hooks/appointments/usePatientAppointments';
import { toZonedTime, format } from 'date-fns-tz';

interface Props {
  onLogout: (patientClinicDataId?: number) => void;
  headerRefetchMethods?: {
    refetchSMSCount: () => Promise<void>;
    refetchVoicemailCount: () => Promise<void>;
  };
  externalPatientId?: number | null;
  externalActivityId?: number | null;
  externalWorkflowId?: number | null;
  onPatientLoaded?: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
  isFromGlobalSearch?: boolean;
}

const PatientEnrollmentForm: React.FC<Props> = ({ onLogout, headerRefetchMethods, externalPatientId, externalActivityId, externalWorkflowId, onPatientLoaded, showBackButton, onBack, isFromGlobalSearch }) => {
  const { user, hasuraToken, token } = useAuth();
  const { toast } = useToast();
  // Use global timer hook for synchronization
  const globalTimer = useGlobalTimelog();

  // Track if save was completed when coming from external source
  const [saveCompleted, setSaveCompleted] = React.useState(false);

  // Editable program eligibility state for ED/EA users
  const [selectedEligibility, setSelectedEligibility] = React.useState<string[]>([]);

  // State for diagnosis causes
  const [diagnosisCauses, setDiagnosisCauses] = React.useState<Array<{ id: number; cause: string }>>([]);

  // State for insurance providers
  const [insuranceProviders, setInsuranceProviders] = React.useState<Array<{ insurance_payor: string; insurance_id: number }>>([]);


  // Use HBox call hook to fetch allowed user roles on page load
  const { hboxCall, canTransfer } = useHBoxCall();

  // Use phone validation hook
  const {
    validatePhoneNumbers,
    isPhoneValid,
    isPhoneInvalid,
    formatPhoneNumber: formatPhone
  } = usePhoneValidation();

  // Use our clean hooks for data and operations - MUST be called before using workflowId
  const {
    // Data
    patientData,
    ccmStatusUpdates,
    rpmStatusUpdates,
    ccmStatusOptions,
    groupOptions,
    clinicNumbers,
    clinicNumbersSources,
    smsClinicNumbers,
    smsNumbersSources,
    editableFields,
    esNamesMapping,

    // State
    patientClinicDataId,
    groupId,
    selectedGroup,
    patientTimezone,
    workflowId,
    isLoading,
    isLoadingGroupChange,
    isDeferredMode,
    hasDeferredTask,
    hasEmptyPatientList,
    noPatientMessage,

    // Sequential loading states
    isLoadingGroups,
    isLoadingPatientData,
    isLoadingCCMData,
    groupsLoaded,
    patientDataLoaded,
    ccmDataLoaded,

    // Actions
    resetFormData,
    triggerSequentialLoading,
    completePatientDataLoading,
    completeCCMDataLoading,
    findNextAvailableGroup,
    switchToNextAvailableGroup,

    // Setters
    setHasEmptyPatientList,

    // Actions
    refetchPatientData,
    refetchEnrollmentDetailsOnly,
    loadDeferredPatient,
    handleBackToNormalPatients,
    handleFieldChange,
    initializeEditableFields,

    // Setters
    setPatientClinicDataId,
    setGroupId,
    setSelectedGroup,
    setCCMStatusUpdates,
    setRPMStatusUpdates,
    setPatientData,
    setEditableFields,
    setIsLoadingGroupChange,
    setClinicNumbers,
    setClinicNumbersSources,
    setSmsClinicNumbers,
    setSmsNumbersSources,

    // Persistent group selection
    setPersistentGroup,
    clearPersistentGroup,
    hasPersistedGroup,

    // Service instance
    esService,

    // Conditional locking functionality
    conditionalLockPatientByGroup,
    isInitialLogin,
    lastLockedGroupId,
    currentPatientId,
    setCurrentPatientId,
    assignmentData
  } = useESPatientData(externalPatientId, onPatientLoaded, externalActivityId, externalWorkflowId);

  // Fetch program type statuses based on workflow - MUST be after workflowId is available
  const { programTypeStatuses, loading: loadingProgramStatuses } = useProgramTypeStatuses(workflowId || undefined, isFromGlobalSearch);
  
  // Fetch workflow statuses for dropdown
  const { workflowStatuses, loading: loadingWorkflowStatuses } = useWorkflowStatusesList();

  // Clinic, Provider & Facility dropdown state for EA/ED users
  const [clinicsList, setClinicsList] = useState<Clinic[]>([]);
  const [providersList, setProvidersList] = useState<Provider[]>([]);
  const [facilitiesList, setFacilitiesList] = useState<Facility[]>([]);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [clinicPopoverOpen, setClinicPopoverOpen] = useState(false);

  // Fetch available clinics on mount for EA/ED users
  useEffect(() => {
    if (user?.type !== 'ED' && user?.type !== 'EA') return;
    const fetchClinics = async () => {
      setLoadingClinics(true);
      try {
        const ddService = serviceFactory.getService(DropdownDataService);
        const response = await ddService.fetchAvailableClinics();
        if (response.success && response.data?.pep_vw_available_clinics) {
          setClinicsList(response.data.pep_vw_available_clinics);
        }
      } catch (err) {
        console.error('Error fetching clinics for dropdown:', err);
      } finally {
        setLoadingClinics(false);
      }
    };
    fetchClinics();
  }, [user?.type]);

  // Fetch providers when patient data loads (using patient's clinic_id)
  useEffect(() => {
    if ((user?.type !== 'ED' && user?.type !== 'EA') || !patientData?.clinic_id) return;
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const ddService = serviceFactory.getService(DropdownDataService);
        const response = await ddService.fetchProvidersByClinic(patientData.clinic_id);
        if (response.success && response.data?.pep_vw_consolidated_data) {
          const providers = response.data.pep_vw_consolidated_data;
          setProvidersList(providers);
          // Auto-select provider based on providerId from enrollment details
          if (patientData.provider_id) {
            const matchedProvider = providers.find(p => p.provider_id === patientData.provider_id);
            if (matchedProvider) {
              handleFieldChange('provider_name', matchedProvider.provider_name);
              handleFieldChange('provider_id', matchedProvider.provider_id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching providers for dropdown:', err);
        setProvidersList([]);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [patientData?.clinic_id, patientData?.provider_id, user?.type]);

  // Fetch facilities when patient data loads (using patient's clinic_id)
  useEffect(() => {
    if ((user?.type !== 'ED' && user?.type !== 'EA') || !patientData?.clinic_id) return;
    const fetchFacilities = async () => {
      setLoadingFacilities(true);
      try {
        const ddService = serviceFactory.getService(DropdownDataService);
        const response = await ddService.fetchFacilitiesByClinic(patientData.clinic_id);
        if (response.success && response.data?.pep_vw_consolidated_data) {
          const facilities = response.data.pep_vw_consolidated_data;
          setFacilitiesList(facilities);
          // Auto-select facility based on clinicFacilityId from enrollment details
          if (patientData.clinicFacilityId || (patientData as any).clinic_facility_id) {
            const facilityId = patientData.clinicFacilityId || (patientData as any).clinic_facility_id;
            const matchedFacility = facilities.find(f => f.facility_id === facilityId);
            if (matchedFacility) {
              handleFieldChange('facility_name', matchedFacility.facility_name);
              handleFieldChange('facility_id', matchedFacility.facility_id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching facilities for dropdown:', err);
        setFacilitiesList([]);
      } finally {
        setLoadingFacilities(false);
      }
    };
    fetchFacilities();
  }, [patientData?.clinic_id, patientData?.clinicFacilityId, user?.type]);

  // Fetch patient appointments
  const { appointments, loading: loadingAppointments, fetchAppointments, clearAppointments } = usePatientAppointments();
  const groupedProgramStatuses = React.useMemo(() => {
    const grouped: Record<string, Array<{
      id: string;
      status: string;
      programType: string;
      programStatusId: number;
    }>> = {};
    
    programTypeStatuses.forEach(status => {
      if (!grouped[status.program_type]) {
        grouped[status.program_type] = [];
      }
      grouped[status.program_type].push({
        id: status.program_status_mapper_id,
        status: status.program_status,
        programType: status.program_type,
        programStatusId: status.program_status_id
      });
    });
    
    return grouped;
  }, [programTypeStatuses]);
  
  // Create a mapping from status string to program_status_id for payload building
  const statusToIdMapping = React.useMemo(() => {
    const mapping: Record<string, number> = {};
    programTypeStatuses.forEach(status => {
      mapping[status.program_status] = status.program_status_id;
    });
    return mapping;
  }, [programTypeStatuses]);
  
  // Create a mapping from program_status_id to program_status for display
  const idToStatusMapping = React.useMemo(() => {
    const mapping: Record<number, string> = {};
    programTypeStatuses.forEach(status => {
      mapping[status.program_status_id] = status.program_status;
    });
    return mapping;
  }, [programTypeStatuses]);
  
  // Extract enrollment attempts from patientData REST API response
  const enrollmentAttemptsByProgramType = React.useMemo(() => {
    if (!patientData) return {};
    
    // The REST API returns enrollment attempts in the response
    const apiResponse = patientData as any;
    const attempts = apiResponse.enrollmentAttemptsByProgramType || {};
    console.log('[PatientEnrollmentForm] Enrollment attempts by program type:', attempts);
    console.log('[PatientEnrollmentForm] Program type keys:', Object.keys(attempts));
    return attempts;
  }, [patientData]);
  
  // Get program type keys dynamically from the enrollment attempts data
  const programTypeKeys = React.useMemo(() => {
    return Object.keys(enrollmentAttemptsByProgramType);
  }, [enrollmentAttemptsByProgramType]);
  
  // State for search filter
  const [statusSearchQuery, setStatusSearchQuery] = React.useState('');
  
  // Track selected statuses per program type (e.g., { "CCM": "Kit Replacement", "RPM": "NANV" })
  const [selectedStatusesByProgramType, setSelectedStatusesByProgramType] = React.useState<Record<string, string>>({});

  // Use patient timer hook for patient-specific timelog (after patientClinicDataId is available)
  const patientTimer = usePatientDetailsTimer(patientClinicDataId);

  const {
    // Form state
    selectedCCMStatus,
    comment,
    appointmentDateTime,
    hasUnsavedChanges,
    isFormLocked,
    selectedWorkflowStatusId,

    // Button states
    isSaving,
    isSavingDemographic,
    saveButtonDisabled,
    transferButtonDisabled,
    nextButtonDisabled,
    isLoadingNext,
    isPatientSaved,

    // Transfer state
    showTransferStatus,
    transferState,
    transferId,
    careSpecialist,
    transferCompleted,
    isTransferInProgress,

    // SMS state
    smsDialogOpen,
    selectedPhoneNumber,
    selectedPhoneType,

    // Group completion dialog state
    showGroupCompletionDialog,
    currentGroupName,
    nextGroupName,

    // Actions
    handleCCMStatusChange,
    handleCommentChange,
    handleSave,
    handleSaveDemographic,
    handleTransferPatient,
    handleTransferAccepted,
    handleCloseTransferStatus,
    handleNextPatient,
    handleGroupSelectionChange,
    handleGroupCompletionConfirm,

    // Setters
    setSelectedCCMStatus,
    setComment,
    setAppointmentDateTime,
    setHasUnsavedChanges,
    setSelectedWorkflowStatusId,
    setSmsDialogOpen,
    setSelectedPhoneNumber,
    setSelectedPhoneType,
    setNextButtonDisabled,
    setSaveButtonDisabled,
    setTransferButtonDisabled,
    setIsFormLocked,
    resetFormState,
    setShowGroupCompletionDialog,
    setCurrentGroupName,
    setNextGroupName,

    // Refs
    transferTimeoutRef
  } = useESPatientOperations(esService);

  // State for appointment scheduling
  const [appointmentData, setAppointmentData] = React.useState<AppointmentData | null>(null);

  // Counter to force AppointmentScheduler remount on Next Patient
  const [nextPatientCounter, setNextPatientCounter] = React.useState(0);

  // Wrapper for handleCCMStatusChange to track selected statuses by program type
  const handleCCMStatusChangeWrapper = (value: string, programType: string) => {
    // Update the selected statuses by program type
    setSelectedStatusesByProgramType(prev => ({
      ...prev,
      [programType]: value
    }));
    handleCCMStatusChange(value);
  };

  // Clear selected statuses when patient changes or form resets
  React.useEffect(() => {
    if (!selectedCCMStatus || !patientClinicDataId) {
      setSelectedStatusesByProgramType({});
    }
  }, [selectedCCMStatus, patientClinicDataId]);

  // Fetch diagnosis causes on mount
  React.useEffect(() => {
    const fetchDiagnosisCauses = async () => {
      if (!hasuraToken) return;
      
      try {
        const causes = await miscGraphqlService.getDiagnosisCauses(hasuraToken);
        setDiagnosisCauses(causes);
      } catch (error) {
        console.error('Error fetching diagnosis causes:', error);
      }
    };
    
    fetchDiagnosisCauses();
  }, [hasuraToken]);

  // Fetch insurance providers on mount
  React.useEffect(() => {
    const fetchInsuranceProviders = async () => {
      if (!hasuraToken) return;
      
      try {
        const providers = await miscGraphqlService.getInsuranceProviders(hasuraToken);
        setInsuranceProviders(providers);
      } catch (error) {
        console.error('Error fetching insurance providers:', error);
      }
    };
    
    fetchInsuranceProviders();
  }, [hasuraToken]);

  // Clear appointment data when patient changes
  React.useEffect(() => {
    setAppointmentData(null);
    // Reset save completed state when patient changes
    setSaveCompleted(false);
  }, [patientClinicDataId]);

  // Fetch patient appointments when patient changes
  React.useEffect(() => {
    if (patientClinicDataId) {
      fetchAppointments(patientClinicDataId);
    } else {
      clearAppointments();
    }
  }, [patientClinicDataId, fetchAppointments, clearAppointments]);

  // Helper function to format appointment time in clinic timezone
  const formatAppointmentTime = (dateTimeString: string, timezone: string) => {
    try {
      const utcDate = new Date(dateTimeString);
      const zonedDate = toZonedTime(utcDate, timezone);
      return format(zonedDate, 'hh:mm a', { timeZone: timezone });
    } catch {
      return 'N/A';
    }
  };

  // Helper function to format appointment date in clinic timezone
  const formatAppointmentDate = (dateTimeString: string, timezone: string) => {
    try {
      const utcDate = new Date(dateTimeString);
      const zonedDate = toZonedTime(utcDate, timezone);
      return format(zonedDate, 'MMM dd, yyyy', { timeZone: timezone });
    } catch {
      return 'N/A';
    }
  };

  // Form lock state - disable form after successful save or transfer
  const formLocked = isFormLocked || isPatientSaved || transferCompleted || transferState === 'accepted';

  // Patient timer navigation control
  useEffect(() => {
    // Enable auto-start to ensure timer starts from 0 when returning to page
    setPatientTimerAutoStart(true);

    // Cleanup function for navigation away
    return () => {
      silentPatientTimerCleanup();
    };
  }, []);

  // Sync program eligibility state when patient data loads
  useEffect(() => {
    if (patientData) {
      const eligibility: string[] = [];
      if (patientData.ccmEligible) eligibility.push('CCM');
      if (patientData.rpmEligible) eligibility.push('RPM');
      if (patientData.pcmEligible) eligibility.push('PCM');
      setSelectedEligibility(eligibility);
    }
  }, [patientData]);

  // Validate phone numbers when patient data loads
  useEffect(() => {
    if (patientData && (editableFields.mobile_phone_number || editableFields.home_phone_number)) {
      const numbersToValidate = [];
      if (editableFields.mobile_phone_number && editableFields.mobile_phone_number !== 'n/a') {
        numbersToValidate.push(editableFields.mobile_phone_number);
      }
      if (editableFields.home_phone_number && editableFields.home_phone_number !== 'n/a') {
        numbersToValidate.push(editableFields.home_phone_number);
      }
      if (numbersToValidate.length > 0) {
        validatePhoneNumbers(numbersToValidate);
      }
    }
  }, [patientData, editableFields.mobile_phone_number, editableFields.home_phone_number, validatePhoneNumbers]);


  // Sync selectedGroup with groupId when groupId changes (e.g., when stored group is invalid)
  useEffect(() => {
    if (groupId && groupOptions.length > 0) {
      const currentGroup = groupOptions.find(g => g.group_id === groupId);
      if (currentGroup && selectedGroup !== groupId.toString()) {
        setSelectedGroup(groupId.toString());
      }
    }
  }, [groupId, groupOptions, selectedGroup, setSelectedGroup]);

  // Utility functions
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handle SMS sending using hbox-sms package
  const handleSendSMS = (phoneType: 'mobile' | 'home', phoneNumber: string) => {
    if (!patientClinicDataId || !token || !hasuraToken || !clinicNumbers || clinicNumbers.length === 0) {
      console.error('Missing required data for SMS modal');
      return;
    }

    // Prepare patient numbers for the modal
    const patientNumbers = [];
    if (editableFields.mobile_phone_number && editableFields.mobile_phone_number !== 'n/a') {
      patientNumbers.push({
        number: editableFields.mobile_phone_number,
        type: 'Mobile Number',
        is_selected: phoneType === 'mobile'
      });
    }
    // if (editableFields.home_phone_number && editableFields.home_phone_number !== 'n/a') {
    //   patientNumbers.push({
    //     number: editableFields.home_phone_number,
    //     type: 'Home Number',
    //     is_selected: phoneType === 'home'
    //   });
    // }
    try {
      openSendSMSModal(
        {
          apiBaseUrl: API_CONFIG.API_BASE,
          graphqlBaseUrl: API_CONFIG.GRAPHQL_ENDPOINT,
          apiToken: token,
          graphqlToken: hasuraToken,
          loggedInUserId: parseInt(user?.id),
          key: 'PEP',
          pepVersion: 'v2'
        },
        {
          patientId: patientClinicDataId,
          patientName: `${editableFields.first_name} ${editableFields.last_name}`,
          patientEmr: patientData?.emr_id || 'N/A',
          patient_clinic_timezone: patientData?.patient_clinic_timezone,
          clinicNumbers: clinicNumbers,
          patientNumbers: patientNumbers,
          clinicId: patientData?.clinic_id,
          key: 'PEP'
        }
      );
    } catch (error) {
      console.error('Error opening SMS modal:', error);
      toast({
        title: "Error",
        description: "Failed to open SMS dialog. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Wrapper functions to pass required parameters to operations
  const handleSaveWrapper = async () => {
    await handleSave(
      patientData,
      patientClinicDataId!,
      groupId!,
      ccmStatusOptions,
      editableFields,
      // Use the dedicated refetchEnrollmentDetailsOnly (no assignment query)
      refetchEnrollmentDetailsOnly,
      statusToIdMapping,
      appointmentData?.availabilityDayTimeId || null,
      appointmentData?.date || '',
      selectedWorkflowStatusId,
      selectedStatusesByProgramType,
      programTypeStatuses,
      workflowId, // Pass workflow ID from URL params for CS/PE users
      appointmentData, // Pass full appointment data for the new API payload
      assignmentData // Pass assignment data for ES users (avoids calling visiblePatient API on save)
    );
    
    // Mark save as completed when coming from external source
    if (showBackButton) {
      setSaveCompleted(true);
    }
  };

  // Check if demographic fields have changed
  const hasDemographicChanges = React.useMemo(() => {
    if (!patientData || !editableFields) return false;
    
    const basicFieldsChanged = (
      editableFields.first_name !== patientData.first_name ||
      editableFields.last_name !== patientData.last_name ||
      editableFields.dob !== patientData.dob ||
      editableFields.gender !== patientData.gender ||
      editableFields.mobile_phone_number !== patientData.mobile_phone_number ||
      editableFields.home_phone_number !== patientData.home_phone_number ||
      editableFields.email !== patientData.email ||
      editableFields.address_line_1 !== patientData.address_line_1 ||
      editableFields.city !== patientData.city ||
      editableFields.state !== patientData.state ||
      editableFields.zip_code !== patientData.zip_code ||
      editableFields.primary_insurance_provider !== patientData.primary_insurance_provider ||
      editableFields.secondary_insurance_provider !== patientData.secondary_insurance_provider ||
      editableFields.primary_insurance_id !== patientData.primary_insurance_id ||
      editableFields.secondary_insurance_id !== patientData.secondary_insurance_id
    );
    
    // For non-VPE users (not ES), also check Primary Dx and Secondary Dx changes
    const diagnosisFieldsChanged = user?.type !== 'ES' && (
      editableFields.primary_dx !== patientData.primary_dx ||
      editableFields.secondary_dx !== patientData.secondary_dx
    );
    
    // For ED/EA users, also check clinic_name, provider_name, emr_id, and program eligibility changes
    const originalEligibility: string[] = [];
    if (patientData.ccmEligible) originalEligibility.push('CCM');
    if (patientData.rpmEligible) originalEligibility.push('RPM');
    if (patientData.pcmEligible) originalEligibility.push('PCM');
    const eligibilityChanged = selectedEligibility.length !== originalEligibility.length ||
      selectedEligibility.some(e => !originalEligibility.includes(e));

    const adminFieldsChanged = (user?.type === 'ED' || user?.type === 'EA') && (
      editableFields.clinic_name !== patientData.clinic_name ||
      editableFields.provider_name !== patientData.provider_name ||
      editableFields.facility_name !== (patientData.facility_name || '') ||
      (editableFields.emr_id || '') !== (patientData.emr_id || '') ||
      eligibilityChanged
    );
    
    return basicFieldsChanged || diagnosisFieldsChanged || adminFieldsChanged;
  }, [patientData, editableFields, user?.type, selectedEligibility]);

  const handleSaveDemographicWrapper = async () => {
    // Validate provider and facility are selected ONLY when clinic name was actually changed
    const clinicChanged = patientData && editableFields.clinic_name !== patientData.clinic_name;
    if (clinicChanged && (!editableFields.provider_name || !editableFields.facility_name)) {
      const missing = [];
      if (!editableFields.provider_name) missing.push('Provider Name');
      if (!editableFields.facility_name) missing.push('Facility Name');
      toast({
        title: "Required Fields Missing",
        description: `Please select ${missing.join(' and ')} before saving.`,
        variant: "destructive",
        duration: 5000
      });
      return;
    }
    const success = await handleSaveDemographic(patientData, editableFields, patientClinicDataId, selectedEligibility);
    if (success) {
      // Reload patient data with updated details
      await refetchPatientData(patientClinicDataId || undefined);
    }
  };

  const handleTransferWrapper = () => {
    handleTransferPatient(patientData, patientClinicDataId!, groupId!);
  };

  const handleNextPatientWrapper = () => {
    console.log('[PatientEnrollmentForm] Starting next patient workflow');
    
    // Explicitly reset appointment data and increment counter to force remount
    setAppointmentData(null);
    setNextPatientCounter(prev => prev + 1);
    
    // Special handling for deferred mode - don't show group completion dialog
    if (isDeferredMode) {
      // For deferred mode, just show empty state when there are no more patients
      // Reset form state first, then load deferred patient
      resetFormState();
      loadDeferredPatient()
        .then(() => {
          // Form state already reset above
        })
        .catch(error => {
          console.error('Error loading next deferred patient:', error);
          toast({
            title: "Error",
            description: "Failed to load next deferred patient",
            variant: "destructive"
          });
        });
    } else {
      // Normal mode - use the existing next patient workflow
      handleNextPatient(
        groupId!,
        async (patientId?: number) => {
          console.log('[PatientEnrollmentForm] Next patient optimized refetch called',
            patientId ? `for ID: ${patientId}` : 'without specific ID');
          // We'll delegate to the refetchPatientData function, which now has caching
          await refetchPatientData(patientId);
        },
        initializeEditableFields,
        setPatientClinicDataId,
        setGroupId,
        setCCMStatusUpdates,
        setPatientData,
        currentPatientId,
        setCurrentPatientId,
        setClinicNumbers,
        setClinicNumbersSources,
        setSmsClinicNumbers,
        setSmsNumbersSources,
        headerRefetchMethods, // Pass header refetch methods for SMS/voicemail counts
        () => setAppointmentData(null) // Reset appointment data callback
      );
    }
  };

  // Handle group completion dialog confirmation
  const handleGroupCompletionConfirmWrapper = async () => {
    if (!esService || !groupId) {
      handleGroupCompletionConfirm();
      return;
    }

    try {
      // For deferred mode, don't show group completion dialog or switch groups
      if (isDeferredMode) {
        // Just close the dialog and show empty state if needed
        handleGroupCompletionConfirm();
        setHasEmptyPatientList(true);
        return;
      }

      const currentGroup = groupOptions.find(g => g.group_id === groupId);
      const nextGroup = await findNextAvailableGroup(groupId);

      if (nextGroup) {
        // Set group names for dialog display
        setCurrentGroupName(currentGroup?.group_name || '');
        setNextGroupName(nextGroup.group_name);

        // Switch to next available group
        const success = await switchToNextAvailableGroup();

        if (success) {
          // Reset and unlock form for new patient after successful group switch
          resetFormState();
          handleGroupCompletionConfirm();
          toast({
            title: "Group Switched",
            description: `Switched to ${nextGroup.group_name} - Patient loaded successfully`
          });
        } else {
          handleGroupCompletionConfirm();
          toast({
            title: "Error",
            description: "Failed to switch to next group",
            variant: "destructive"
          });
        }
      } else {
        // No more groups with patients - show congrats message
        handleGroupCompletionConfirm();
        setHasEmptyPatientList(true);
      }
    } catch (error) {
      console.error('Error switching to next group:', error);
      handleGroupCompletionConfirm();
      toast({
        title: "Error",
        description: "Failed to switch to next group",
        variant: "destructive"
      });
    }
  };

  const handleGroupChangeWrapper = (newGroupId: string) => {
    // Validate that the selected group exists in current options
    const newGroupIdNumber = parseInt(newGroupId);
    const groupExists = groupOptions.some(g => g.group_id === newGroupIdNumber);
    
    if (!groupExists) {
      toast({
        title: "Invalid Group Selection",
        description: "The selected group is no longer available. Please refresh the page.",
        variant: "destructive"
      });
      return;
    }

    // Pass an optimized refetch function to prevent duplicate API calls
    handleGroupSelectionChange(
      newGroupId,
      patientClinicDataId,
      // Using optimized function that avoids redundant API calls
      async (patientId?: number) => {
        // Skip refetch since handleGroupSelectionChange already loads data
      },
      setPatientClinicDataId,
      setGroupId,
      setSelectedGroup,
      setIsLoadingGroupChange,
      initializeEditableFields,
      setCCMStatusUpdates,
      setPersistentGroup, // Save to session storage when user manually changes group
      groupOptions, // Add group options for name lookup
      conditionalLockPatientByGroup, // Pass conditional locking function
      currentPatientId, // Pass current patient ID for unlock functionality
      setCurrentPatientId, // Pass setter for patient ID tracking
      setClinicNumbers, // Pass clinic numbers setter
      setClinicNumbersSources, // Pass clinic number sources setter
      setSmsClinicNumbers, // Pass SMS numbers setter
      setSmsNumbersSources // Pass SMS number sources setter
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex items-center justify-center" data-testid="es-form-loading-container">
        <div className="text-center" data-testid="es-form-loading-content">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" data-testid="es-form-loading-spinner"></div>
          <p className="text-gray-600" data-testid="es-form-loading-text">Loading patient data...</p>
        </div>
      </div>
    );
  }

  // Show empty state when no patients are available for this enrollment specialist
  if (!isLoading && hasEmptyPatientList) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50"
        data-deferred-mode={isDeferredMode ? "true" : "false"}
        data-testid="es-form-empty-state-container"
      >

        {/* Always show back button when in deferred mode */}
        {isDeferredMode && !showBackButton && (
          <div className="absolute top-8 left-8 z-10" data-testid="es-form-empty-back-button-wrapper">
            <Button
              id="es-back-to-normal-button-empty"
              name="backToNormalEmpty"
              data-testid="es-back-to-normal-button-empty"
              onClick={() => {
                resetFormState(); // Reset form state before going back to normal patients
                handleBackToNormalPatients();
              }}
              variant="outline"
              className="bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        )}

        {/* Show back button when loaded from external source */}
        {showBackButton && onBack && (
          <div className="absolute top-8 left-8 z-10" data-testid="es-form-external-back-button-wrapper">
            <Button
              onClick={onBack}
              variant="outline"
              className="bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black"
              data-testid="es-external-back-button-empty"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        )}

        <EmptyState
          id="es-empty-patient-list"
          data-testid="es-empty-patient-list"
          title={noPatientMessage ? "Please Wait" : "Congrats!"}
          description={noPatientMessage || "You've completed all the assigned patients for this week."}
          icon={noPatientMessage ? <Clock className="h-20 w-20 text-blue-500" /> : <CheckCircle className="h-20 w-20 text-green-500" />}
          className="min-h-screen bg-transparent"
        // Removed showBackButton prop as we're handling it separately above
        // showBackButton={isDeferredMode}
        // onBackClick={isDeferredMode ? handleBackToNormalPatients : undefined}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="es-form-main-container">
      {/* Main Content */}
      <main className="mx-auto" data-testid="es-form-content">
        {/* Deferred Patients Button and Timer */}
        <div className="flex justify-between items-center mb-3" data-testid="es-form-header-section">
          {/* Left side - Back button */}
          <div className="flex-shrink-0" data-testid="es-form-action-buttons-left">

            {/* Show back button when loaded from external source */}
            {showBackButton && onBack && (
              <Button
                onClick={onBack}
                variant="outline"
                className="bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black"
                data-testid="es-external-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}

            {/* Always show back button when in deferred mode */}
            {!showBackButton && isDeferredMode && (
              <Button
                id="es-back-to-normal-button"
                name="backToNormal"
                data-testid="es-back-to-normal-button"
                onClick={() => {
                  resetFormState(); // Reset form state before going back to normal patients
                  handleBackToNormalPatients();
                }}
                variant="outline"
                className="bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}

            {/* Placeholder div when no button is shown */}
            {!hasDeferredTask && !isDeferredMode && !showBackButton && <div></div>}
          </div>

          {/* Right side - Patient Timer (always positioned on the right) */}
          <div className="flex-shrink-0" data-testid="es-form-timer-section">
            <PatientDetailsTimer patientId={patientClinicDataId} data-testid="es-form-patient-timer" />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="space-y-6" data-testid="es-form-sections">


            {/* Phase 2: Patient Information Form - Only show when groups are loaded */}
            {groupsLoaded && (
              <>
                {isLoadingPatientData && (
                  <div className="flex items-center justify-center min-h-[60vh]" data-testid="es-next-patient-loading">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                      <p className="text-gray-600 text-lg">Loading patient data...</p>
                      <p className="text-gray-500 mt-2">Please wait while we retrieve the patient information</p>
                    </div>
                  </div>
                )}

                {patientData && !isLoadingGroupChange && (
                  <Card className="bg-white shadow-lg border-0 rounded-2xl">
                    <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
                      <CardTitle className="flex items-center space-x-3">
                        <User className="h-6 w-6 text-white" />
                        <span className="text-xl font-bold">Patient Information</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-8">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Column - Personal Information */}
                        <div className="space-y-6">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">First Name</Label>
                              <Input
                                value={editableFields.first_name}
                                onChange={e => handleFieldChange('first_name', e.target.value)}
                                className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                                disabled={formLocked}
                                readOnly={formLocked}
                              />
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Last Name</Label>
                              <Input
                                value={editableFields.last_name}
                                onChange={e => handleFieldChange('last_name', e.target.value)}
                                className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                                disabled={formLocked}
                                readOnly={formLocked}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Date of Birth</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full justify-start text-left font-normal mt-1",
                                      !editableFields.dob && "text-muted-foreground",
                                      formLocked && "bg-gray-50 cursor-not-allowed"
                                    )}
                                    disabled={isDeferredMode || formLocked}
                                  >
                                    <Calendar className="mr-2 h-4 w-4" />
                                    {editableFields.dob ? (
                                      new Date(editableFields.dob).toLocaleDateString('en-US', {
                                        month: '2-digit',
                                        day: '2-digit',
                                        year: 'numeric'
                                      })
                                    ) : (
                                      <span>Pick a date</span>
                                    )}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="single"
                                    selected={editableFields.dob ? new Date(editableFields.dob) : undefined}
                                    onSelect={(date) => {
                                      if (date) {
                                        handleFieldChange('dob', date.toISOString().split('T')[0]);
                                      }
                                    }}
                                    disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                                    initialFocus
                                    className={cn("p-3 pointer-events-auto")}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Gender</Label>
                              <Select
                                value={editableFields.gender}
                                onValueChange={value => handleFieldChange('gender', value)}
                                disabled={isDeferredMode || formLocked}
                              >
                                <SelectTrigger className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}>
                                  <SelectValue placeholder="Select Gender" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="M">Male</SelectItem>
                                  <SelectItem value="F">Female</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Mobile Number</Label>
                              <div className="flex items-center space-x-2 mt-1">
                                <Input
                                  value={editableFields.mobile_phone_number}
                                  onChange={e => handleFieldChange('mobile_phone_number', e.target.value)}
                                  className={`${formLocked ? 'bg-gray-50' : ''} ${isPhoneInvalid(editableFields.mobile_phone_number) ? 'border-red-500' : ''}`}
                                  disabled={formLocked}
                                  readOnly={formLocked}
                                />
                                {isPhoneInvalid(editableFields.mobile_phone_number) && (
                                  <>
                                    <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                                    <Badge variant="destructive" className="text-xs px-2 py-0 shrink-0">
                                      Invalid
                                    </Badge>
                                  </>
                                )}
                                {editableFields.mobile_phone_number && editableFields.mobile_phone_number !== 'n/a' && isPhoneValid(editableFields.mobile_phone_number) && (
                                  <>
                                    <CallHandler
                                      phoneNumber={editableFields.mobile_phone_number}
                                      patientName={`${editableFields.first_name} ${editableFields.last_name}`}
                                      patientId={patientClinicDataId}
                                      alternatePhone={editableFields.home_phone_number}
                                      emergencyPhone={patientData?.emergency_mobile_phone}
                                      emergencyContactName={patientData?.emergency_first_name && patientData?.emergency_last_name ? `${patientData.emergency_first_name} ${patientData.emergency_last_name}` : ''}
                                      hccId={patientData?.clinic_id}
                                      clinicName={editableFields.clinic_name}
                                      timezone={patientData?.patient_clinic_timezone || 'US/Eastern'}
                                      clinicNumbers={clinicNumbers?.length ? clinicNumbers : []}
                                      clinicNumbersSources={clinicNumbersSources}
                                      disabled={formLocked}
                                    />
                                    <Button
                                      size="icon"
                                      variant="outline"
                                      className="shrink-0"
                                      onClick={() => {
                                        navigator.clipboard.writeText(editableFields.mobile_phone_number);
                                        toast({
                                          title: "Copied",
                                          description: "Mobile number copied to clipboard"
                                        });
                                      }}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-block">
                                            <Button
                                              size="icon"
                                              variant="outline"
                                              className="shrink-0"
                                              disabled={formLocked || !editableFields.mobile_phone_number || editableFields.mobile_phone_number === 'n/a' || !clinicNumbers || clinicNumbers.length === 0}
                                                onClick={() => handleSendSMS('mobile', editableFields.mobile_phone_number)}
                                            >
                                              <MessageSquare className="h-4 w-4" />
                                            </Button>
                                          </span>
                                        </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Send SMS</p>
                                          </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </>
                                )}
                              </div>
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Home Phone Number</Label>
                              <div className="flex items-center space-x-2 mt-1">
                                <Input
                                  value={editableFields.home_phone_number}
                                  onChange={e => handleFieldChange('home_phone_number', e.target.value)}
                                  className={`${formLocked ? 'bg-gray-50' : ''} ${isPhoneInvalid(editableFields.home_phone_number) ? 'border-red-500' : ''}`}
                                  disabled={formLocked}
                                  readOnly={formLocked}
                                />
                                {isPhoneInvalid(editableFields.home_phone_number) && (
                                  <>
                                    <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                                    <Badge variant="destructive" className="text-xs px-2 py-0 shrink-0">
                                      Invalid
                                    </Badge>
                                  </>
                                )}
                                {editableFields.home_phone_number && editableFields.home_phone_number !== 'n/a' && isPhoneValid(editableFields.home_phone_number) && (
                                  <>
                                    <CallHandler
                                      phoneNumber={editableFields.home_phone_number}
                                      patientName={`${editableFields.first_name} ${editableFields.last_name}`}
                                      patientId={patientClinicDataId}
                                      alternatePhone={editableFields.mobile_phone_number}
                                      emergencyPhone={patientData?.emergency_mobile_phone}
                                      emergencyContactName={patientData?.emergency_first_name && patientData?.emergency_last_name ? `${patientData.emergency_first_name} ${patientData.emergency_last_name}` : ''}
                                      hccId={patientData?.clinic_id}
                                      clinicName={editableFields.clinic_name}
                                      timezone={patientData?.patient_clinic_timezone || 'US/Eastern'}
                                      clinicNumbers={clinicNumbers?.length ? clinicNumbers : []}
                                      clinicNumbersSources={clinicNumbersSources}
                                      disabled={formLocked}
                                    />
                                    <Button
                                      size="icon"
                                      variant="outline"
                                      className="shrink-0"
                                      onClick={() => {
                                        navigator.clipboard.writeText(editableFields.home_phone_number);
                                        toast({
                                          title: "Copied",
                                          description: "Home phone number copied to clipboard"
                                        });
                                      }}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    {/* <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-block">
                                            <Button
                                              size="icon"
                                              variant="outline"
                                              className="shrink-0"
                                              disabled={formLocked || !editableFields.home_phone_number || editableFields.home_phone_number === 'n/a' || !clinicNumbers || clinicNumbers.length === 0}
                                                onClick={() => handleSendSMS('home', editableFields.home_phone_number)}
                                            >
                                              <MessageSquare className="h-4 w-4" />
                                            </Button>
                                          </span>
                                        </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Send SMS</p>
                                          </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider> */}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div>
                            <Label className="text-sm font-semibold text-gray-700">Email Address</Label>
                            <Input
                              value={editableFields.email}
                              onChange={e => handleFieldChange('email', e.target.value)}
                              className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                              disabled={formLocked}
                              readOnly={formLocked}
                            />
                          </div>

                          <div>
                            <Label className="text-sm font-semibold text-gray-700">Address</Label>
                            <Input
                              value={editableFields.address_line_1}
                              onChange={e => handleFieldChange('address_line_1', e.target.value)}
                              className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                              disabled={formLocked}
                              readOnly={formLocked}
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">City</Label>
                              <Input
                                value={editableFields.city}
                                onChange={e => handleFieldChange('city', e.target.value)}
                                className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                                disabled={formLocked}
                                readOnly={formLocked}
                              />
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">State</Label>
                              <Input
                                value={editableFields.state}
                                onChange={e => handleFieldChange('state', e.target.value)}
                                className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                                disabled={formLocked}
                                readOnly={formLocked}
                              />
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Zip Code</Label>
                              <Input
                                value={editableFields.zip_code}
                                onChange={e => handleFieldChange('zip_code', e.target.value)}
                                className={`mt-1 ${isFormLocked ? 'bg-gray-50' : ''}`}
                                disabled={isFormLocked}
                                readOnly={isFormLocked}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Medical Information */}
                        <div className="space-y-6">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Clinic Name</Label>
                              {(user?.type === 'ED' || user?.type === 'EA') && !formLocked ? (
                                <Popover open={clinicPopoverOpen} onOpenChange={setClinicPopoverOpen}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      aria-expanded={clinicPopoverOpen}
                                      className="mt-1 w-full justify-between font-normal overflow-hidden"
                                    >
                                      <span className="truncate">{editableFields.clinic_name || (loadingClinics ? "Loading clinics..." : "Select Clinic")}</span>
                                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-50" align="start">
                                    <Command>
                                      <CommandInput placeholder="Search clinic..." />
                                      <CommandList>
                                        <CommandEmpty>No clinic found.</CommandEmpty>
                                        <CommandGroup>
                                          {clinicsList.map((clinic) => (
                                            <CommandItem
                                              key={clinic.clinic_id}
                                              value={clinic.clinic_name}
                                              onSelect={() => {
                                                handleFieldChange('clinic_name', clinic.clinic_name);
                                                handleFieldChange('clinic_id', clinic.clinic_id);
                                                handleFieldChange('provider_name', '');
                                                handleFieldChange('provider_id', undefined);
                                                handleFieldChange('facility_name', '');
                                                handleFieldChange('facility_id', undefined);
                                                toast({
                                                  title: "Action Required",
                                                  description: "Please select Provider Name and Facility Name for the updated clinic.",
                                                  variant: "destructive",
                                                  duration: 5000
                                                });
                                                const ddService = serviceFactory.getService(DropdownDataService);
                                                setLoadingProviders(true);
                                                setLoadingFacilities(true);
                                                ddService.fetchProvidersByClinic(clinic.clinic_id)
                                                  .then(res => {
                                                    if (res.success && res.data?.pep_vw_consolidated_data) {
                                                      setProvidersList(res.data.pep_vw_consolidated_data);
                                                    }
                                                  })
                                                  .catch(() => setProvidersList([]))
                                                  .finally(() => setLoadingProviders(false));
                                                ddService.fetchFacilitiesByClinic(clinic.clinic_id)
                                                  .then(res => {
                                                    if (res.success && res.data?.pep_vw_consolidated_data) {
                                                      setFacilitiesList(res.data.pep_vw_consolidated_data);
                                                    }
                                                  })
                                                  .catch(() => setFacilitiesList([]))
                                                  .finally(() => setLoadingFacilities(false));
                                                setClinicPopoverOpen(false);
                                              }}
                                            >
                                              <Check className={cn("mr-2 h-4 w-4", editableFields.clinic_name === clinic.clinic_name ? "opacity-100" : "opacity-0")} />
                                              {clinic.clinic_name}
                                            </CommandItem>
                                          ))}
                                        </CommandGroup>
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              ) : (
                                <Input
                                  value={editableFields.clinic_name}
                                  className="mt-1 bg-gray-50"
                                  readOnly
                                  disabled
                                />
                              )}
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Provider Name</Label>
                              {(user?.type === 'ED' || user?.type === 'EA') && !formLocked ? (
                                <Select
                                  value={editableFields.provider_name}
                                  onValueChange={(value) => {
                                    handleFieldChange('provider_name', value);
                                    const selected = providersList.find(p => p.provider_name === value);
                                    if (selected) handleFieldChange('provider_id', selected.provider_id);
                                  }}
                                >
                                  <SelectTrigger className="mt-1 truncate">
                                    <SelectValue placeholder={loadingProviders ? "Loading providers..." : "Select Provider"} />
                                  </SelectTrigger>
                                  <SelectContent className="bg-background z-50 max-h-60">
                                    {providersList.map((provider) => (
                                      <SelectItem key={provider.provider_id} value={provider.provider_name}>
                                        {provider.provider_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  value={editableFields.provider_name}
                                  className="mt-1 bg-gray-50"
                                  readOnly
                                  disabled
                                />
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Facility Name</Label>
                              {(user?.type === 'ED' || user?.type === 'EA') && !formLocked ? (
                                <Select
                                  value={editableFields.facility_name}
                                  onValueChange={(value) => {
                                    handleFieldChange('facility_name', value);
                                    const selected = facilitiesList.find(f => f.facility_name === value);
                                    if (selected) handleFieldChange('facility_id', selected.facility_id);
                                  }}
                                >
                                  <SelectTrigger className="mt-1 truncate">
                                    <SelectValue placeholder={loadingFacilities ? "Loading facilities..." : "Select Facility"} />
                                  </SelectTrigger>
                                  <SelectContent className="bg-background z-50 max-h-60">
                                    {facilitiesList.map((facility) => (
                                      <SelectItem key={facility.facility_id} value={facility.facility_name}>
                                        {facility.facility_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  value={editableFields.facility_name || 'N/A'}
                                  className="mt-1 bg-gray-50"
                                  readOnly
                                  disabled
                                />
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Primary Dx</Label>
                              <Select 
                                value={editableFields.primary_dx ? String(editableFields.primary_dx) : ''} 
                                onValueChange={(value) => handleFieldChange('primary_dx', value)}
                                disabled={user?.type === 'ES' || formLocked}
                              >
                                <SelectTrigger className={`mt-1 ${user?.type === 'ES' || formLocked ? 'bg-gray-50' : ''}`}>
                                  <SelectValue placeholder="Select Primary Dx" />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 max-h-64 overflow-y-auto">
                                  {diagnosisCauses.map((diagnosis) => (
                                    <SelectItem key={diagnosis.id} value={String(diagnosis.id)}>
                                      {diagnosis.cause}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Secondary Dx</Label>
                              <Select 
                                value={editableFields.secondary_dx ? String(editableFields.secondary_dx) : ''} 
                                onValueChange={(value) => handleFieldChange('secondary_dx', value)}
                                disabled={user?.type === 'ES' || formLocked}
                              >
                                <SelectTrigger className={`mt-1 ${user?.type === 'ES' || formLocked ? 'bg-gray-50' : ''}`}>
                                  <SelectValue placeholder="Select Secondary Dx" />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 max-h-64 overflow-y-auto">
                                  {diagnosisCauses.map((diagnosis) => (
                                    <SelectItem key={diagnosis.id} value={String(diagnosis.id)}>
                                      {diagnosis.cause}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Primary Insurance Provider</Label>
                              <Select 
                                value={editableFields.primary_insurance_provider && 
                                      editableFields.primary_insurance_provider.toLowerCase() !== 'not available' && 
                                      editableFields.primary_insurance_provider.trim() !== '' 
                                      ? editableFields.primary_insurance_provider 
                                      : ''} 
                                onValueChange={(value) => handleFieldChange('primary_insurance_provider', value)}
                                disabled={formLocked}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select Primary Insurance Provider" />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 max-h-64 overflow-y-auto">
                                  {insuranceProviders
                                    .filter(provider => provider.insurance_payor && provider.insurance_payor.trim() !== '')
                                    .map((provider) => (
                                      <SelectItem key={provider.insurance_id} value={provider.insurance_payor}>
                                        {provider.insurance_payor}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Primary Insurance Number</Label>
                              <Input
                                value={editableFields.primary_insurance_id}
                                onChange={e => handleFieldChange('primary_insurance_id', e.target.value)}
                                className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                                disabled={formLocked}
                                readOnly={formLocked}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Secondary Insurance Provider</Label>
                              <Select 
                                value={editableFields.secondary_insurance_provider && 
                                      editableFields.secondary_insurance_provider.toLowerCase() !== 'not available' && 
                                      editableFields.secondary_insurance_provider.trim() !== '' 
                                      ? editableFields.secondary_insurance_provider 
                                      : ''} 
                                onValueChange={(value) => handleFieldChange('secondary_insurance_provider', value)}
                                disabled={formLocked}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select Secondary Insurance Provider" />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 max-h-64 overflow-y-auto">
                                  {insuranceProviders
                                    .filter(provider => provider.insurance_payor && provider.insurance_payor.trim() !== '')
                                    .map((provider) => (
                                      <SelectItem key={provider.insurance_id} value={provider.insurance_payor}>
                                        {provider.insurance_payor}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Secondary Insurance Number</Label>
                              <Input
                                value={editableFields.secondary_insurance_id}
                                onChange={e => handleFieldChange('secondary_insurance_id', e.target.value)}
                                className={`mt-1 ${formLocked ? 'bg-gray-50' : ''}`}
                                disabled={formLocked}
                                readOnly={formLocked}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">Program Eligibility</Label>
                              {(user?.type === 'ED' || user?.type === 'EA') && !formLocked ? (
                                <MultiSelect
                                  options={[
                                    { value: 'CCM', label: 'CCM' },
                                    { value: 'RPM', label: 'RPM' },
                                    { value: 'PCM', label: 'PCM' }
                                  ]}
                                  value={selectedEligibility}
                                  onChange={(val) => setSelectedEligibility(val as string[])}
                                  placeholder="Select Program Eligibility"
                                  searchable={false}
                                  className="mt-1"
                                />
                              ) : (
                                <div className="mt-1 flex flex-wrap gap-2 min-h-[40px] items-center">
                                  {!patientData.ccmEligible && !patientData.rpmEligible && !patientData.pcmEligible ? (
                                    <span className="text-sm text-gray-500">-</span>
                                  ) : (
                                    <>
                                      {patientData.ccmEligible && (
                                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 px-3 py-1">CCM</Badge>
                                      )}
                                      {patientData.rpmEligible && (
                                        <Badge variant="secondary" className="bg-green-100 text-green-800 px-3 py-1">RPM</Badge>
                                      )}
                                      {patientData.pcmEligible && (
                                        <Badge variant="secondary" className="bg-purple-100 text-purple-800 px-3 py-1">PCM</Badge>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <div>
                              <Label className="text-sm font-semibold text-gray-700">EMR ID</Label>
                              <Input
                                value={editableFields.emr_id || patientData?.emr_id || 'N/A'}
                                onChange={e => handleFieldChange('emr_id', e.target.value)}
                                className={`mt-1 ${(user?.type !== 'ED' && user?.type !== 'EA') || formLocked ? 'bg-gray-50' : ''}`}
                                readOnly={(user?.type !== 'ED' && user?.type !== 'EA') || formLocked}
                                disabled={(user?.type !== 'ED' && user?.type !== 'EA') || formLocked}
                              />
                            </div>
                          </div>

                          {/* Save Demographics Button */}
                          <div className="mt-6">
                            <Button
                              onClick={handleSaveDemographicWrapper}
                              disabled={!hasDemographicChanges || !patientData || isSavingDemographic}
                              className="w-full sm:w-auto"
                            >
                              {isSavingDemographic ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="h-4 w-4 mr-2" />
                                  Save Demographic Changes
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Enrollment Attempts - Dynamically show CCM and RPM tabs based on available data */}
            {patientData && !isLoadingGroupChange && (
              <>
                {isLoadingCCMData && (
                  <Card className="bg-white shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-8">
                      <div className="flex flex-col items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                        <p className="text-gray-600 text-lg">Loading enrollment data...</p>
                        <p className="text-gray-500 mt-2">Please wait while we retrieve the enrollment history</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {patientData && !isLoadingCCMData && (programTypeKeys.length > 0 || appointments.length > 0) && (
                  <Card className="bg-white shadow-lg border-0 rounded-2xl">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-purple-600" />
                        <span>Enrollment Attempts</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <Tabs defaultValue={programTypeKeys.length > 0 ? programTypeKeys[0] : 'appointments'} className="w-full">
                        <TabsList className={`grid w-full mb-6`} style={{ gridTemplateColumns: `repeat(${programTypeKeys.length + 1}, minmax(0, 1fr))` }}>
                          {programTypeKeys.map(key => (
                            <TabsTrigger key={key} value={key}>
                              {key} Enrollment Attempts
                            </TabsTrigger>
                          ))}
                          <TabsTrigger value="appointments">
                            Appointments
                          </TabsTrigger>
                        </TabsList>
                        
                        {programTypeKeys.map(key => {
                          const attempts = enrollmentAttemptsByProgramType[key] || [];
                          const bgColor = key === 'CCM' ? 'bg-purple-50' : key === 'RPM' ? 'bg-blue-50' : 'bg-green-50';
                          const textColor = key === 'CCM' ? 'text-purple-800' : key === 'RPM' ? 'text-blue-800' : 'text-green-800';
                          const badgeBg = key === 'CCM' ? 'bg-yellow-100 text-yellow-800' : key === 'RPM' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
                          
                          return (
                            <TabsContent key={key} value={key} className="space-y-4">
                              {attempts && attempts.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full">
                                    <thead className={bgColor}>
                                      <tr>
                                        <th className={`text-left py-3 px-4 font-semibold ${textColor}`}>Attempt to Enroll</th>
                                        <th className={`text-left py-3 px-4 font-semibold ${textColor}`}>Date & Time</th>
                                        <th className={`text-left py-3 px-4 font-semibold ${textColor}`}>Enrollment Status</th>
                                        <th className={`text-left py-3 px-4 font-semibold ${textColor}`}>Enrollment Comments</th>
                                        <th className={`text-left py-3 px-4 font-semibold ${textColor}`}>Updated By</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {attempts.slice().sort((a, b) => {
                                        const idA = a.enrollmentUpdateId || a.id || 0;
                                        const idB = b.enrollmentUpdateId || b.id || 0;
                                        return idB - idA;
                                      }).map((update, index) => {
                                        const attemptNumber = update.communicationAttempt || update.id || (attempts.length - index);
                                        const dateTime = update.formattedDateTime || update.updatedDatetime || update.timestamp;
                                        // Map programStatusId to program_status from GraphQL query
                                        const status = update.programStatusId 
                                          ? (idToStatusMapping[update.programStatusId] || update.programStatusName || update.workflowStatusName || update.status)
                                          : (update.programStatusName || update.workflowStatusName || update.status);
                                        const userId = update.updatedBy || update.user_id;
                                        const userName = update.updatedByName || (esNamesMapping[userId] || `User ${userId}`);
                                        
                                        return (
                                          <tr key={update.enrollmentUpdateId || update.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-3 px-4 font-semibold">{attemptNumber}</td>
                                            <td className="py-3 px-4">{dateTime || 'N/A'}</td>
                                            <td className="py-3 px-4">
                                              <Badge variant="outline" className={badgeBg}>
                                                {status || 'N/A'}
                                              </Badge>
                                            </td>
                                            <td className="py-3 px-4">{update.comment || 'N/A'}</td>
                                            <td className="py-3 px-4 text-sm text-gray-600">
                                              {userName}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                  <p className="text-gray-500 text-lg">No {key} enrollment attempts yet</p>
                                  <p className="text-gray-400 text-sm mt-2">Enrollment attempts will appear here after you save patient data</p>
                                </div>
                              )}
                            </TabsContent>
                          );
                        })}

                        {/* Appointments Tab */}
                        <TabsContent value="appointments" className="space-y-4">
                          {loadingAppointments ? (
                            <div className="flex flex-col items-center justify-center py-10">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
                              <p className="text-gray-600 text-lg">Loading appointments...</p>
                            </div>
                          ) : appointments && appointments.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-orange-50">
                                  <tr>
                                    <th className="text-left py-3 px-4 font-semibold text-orange-800">Appointment Date</th>
                                    <th className="text-left py-3 px-4 font-semibold text-orange-800">Appointment Time</th>
                                    <th className="text-left py-3 px-4 font-semibold text-orange-800">Type</th>
                                    <th className="text-left py-3 px-4 font-semibold text-orange-800">User/Facility</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {appointments.map((appointment) => {
                                    const timezone = appointment.clinic_timezone || 'UTC';
                                    const appointmentDate = formatAppointmentDate(appointment.appointment_date_start_time, timezone);
                                    const startTime = formatAppointmentTime(appointment.appointment_date_start_time, timezone);
                                    const endTime = formatAppointmentTime(appointment.appointment_date_end_time, timezone);
                                    const appointmentType = appointment.appointment_type === 1 ? 'Virtual' : 'In-Person';
                                    const userOrFacility = appointment.appointment_type === 1 
                                      ? (appointment.user_name?.trim() || 'N/A')
                                      : (appointment.facility_name || 'N/A');
                                    
                                    return (
                                      <tr key={appointment.appointment_id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-3 px-4">{appointmentDate}</td>
                                        <td className="py-3 px-4">{startTime} - {endTime}</td>
                                        <td className="py-3 px-4">
                                          <Badge variant="outline" className={appointment.appointment_type === 1 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                                            {appointmentType}
                                          </Badge>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-gray-600">
                                          {userOrFacility}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-8">
                              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500 text-lg">No appointments scheduled</p>
                              <p className="text-gray-400 text-sm mt-2">Appointments will appear here once scheduled</p>
                            </div>
                          )}
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                )}
                
                {patientData && !isLoadingCCMData && programTypeKeys.length === 0 && appointments.length === 0 && (
                  <Card className="bg-white shadow-lg border-0 rounded-2xl">
                    <CardContent className="p-6">
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-lg">No enrollment attempts yet</p>
                        <p className="text-gray-400 text-sm mt-2">Enrollment attempts will appear here after you save patient data</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

          </div>

          {/* CCM Status and Comment - Show when patient data exists, disable inputs when form is locked */}
          {patientData && !isLoadingGroupChange && (
            <Card className="bg-white shadow-lg border-0 rounded-2xl min-w-[400px] h-[100%] sticky top-[70px]">
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-t-xl">
                <CardTitle className="text-xl font-bold">Enrollment Status & Comments</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className={`grid gap-6 ${(selectedCCMStatus === 'Consent' || selectedCCMStatus === 'Escalate') ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-gray-700">Select Enrollment Status <span className="text-red-500">*</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          disabled={formLocked}
                          className="w-full justify-between mt-1 h-auto min-h-10 py-2"
                        >
                          <span className="text-left flex-1">
                            {Object.keys(selectedStatusesByProgramType).length > 0 ? (
                              <span className="flex flex-wrap gap-1">
                                {Object.entries(selectedStatusesByProgramType).map(([pt, status], idx, arr) => (
                                  <span key={pt} className="text-sm">
                                    {pt}: {status}{idx < arr.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                {Object.keys(groupedProgramStatuses).length > 0 ? "No Status Selected" : "Select Enrollment Status"}
                              </span>
                            )}
                          </span>
                          <svg className="ml-2 h-4 w-4 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white z-50" align="start">
                        <div className="sticky top-0 z-10 bg-background p-2 border-b">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search statuses..."
                              className="w-full px-3 py-2 pl-8 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              value={statusSearchQuery}
                              onChange={(e) => setStatusSearchQuery(e.target.value)}
                            />
                            <svg
                              className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                          {loadingProgramStatuses ? (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              Loading statuses...
                            </div>
                          ) : Object.keys(groupedProgramStatuses).length > 0 ? (
                            Object.entries(groupedProgramStatuses).map(([programType, statuses]) => {
                              // Filter statuses based on search query only (don't hide selected items)
                              const filteredStatuses = statuses.filter(option => {
                                const currentCCMStatus = (patientData as any)?.ccm_status_value;
                                const isCurrentStatus = option.status === currentCCMStatus;
                                const matchesSearch = statusSearchQuery.trim() === '' || 
                                  option.status.toLowerCase().includes(statusSearchQuery.toLowerCase()) ||
                                  programType.toLowerCase().includes(statusSearchQuery.toLowerCase());
                                
                                return !isCurrentStatus && matchesSearch;
                              });

                              // Only show the program type section if it has filtered statuses
                              if (filteredStatuses.length === 0) return null;

                              return (
                                <div key={programType} className="py-2">
                                  <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground bg-muted/50">
                                    {programType}
                                  </div>
                                  <div className="py-1">
                                    {filteredStatuses.map(option => {
                                      const isSelected = selectedStatusesByProgramType[programType] === option.status;
                                      return (
                                        <div
                                          key={option.id}
                                          className={cn(
                                            "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                            isSelected && "bg-accent"
                                          )}
                                          onClick={() => {
                                            if (isSelected) {
                                              // Deselect if already selected
                                              setSelectedStatusesByProgramType(prev => {
                                                const newState = { ...prev };
                                                delete newState[programType];
                                                return newState;
                                              });
                                              // Clear selectedCCMStatus if no statuses selected
                                              if (Object.keys(selectedStatusesByProgramType).length <= 1) {
                                                setSelectedCCMStatus('');
                                              }
                                            } else {
                                              handleCCMStatusChangeWrapper(option.status, programType);
                                            }
                                          }}
                                          data-testid={`ccm-status-option-${option.id}`}
                                        >
                                          <span className="flex-1">{option.status}</span>
                                          {isSelected && (
                                            <CheckCircle className="h-4 w-4 text-primary ml-2" />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              No statuses available
                            </div>
                          )}
                          {!loadingProgramStatuses && 
                          Object.keys(groupedProgramStatuses).length > 0 && 
                          Object.entries(groupedProgramStatuses).every(([programType, statuses]) => 
                            statuses.filter(option => {
                              const currentCCMStatus = (patientData as any)?.ccm_status_value;
                              const isCurrentStatus = option.status === currentCCMStatus;
                              const matchesSearch = statusSearchQuery.trim() === '' || 
                                option.status.toLowerCase().includes(statusSearchQuery.toLowerCase()) ||
                                programType.toLowerCase().includes(statusSearchQuery.toLowerCase());
                              return !isCurrentStatus && matchesSearch;
                            }).length === 0
                          ) && (
                            <div className="p-4 text-center text-sm text-muted-foreground">
                              No matching statuses found
                            </div>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  </div>

                  {/* Workflow Status Dropdown */}
                <div>
                  <Label className="text-sm font-semibold text-gray-700">
                    Select Workflow Status <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={selectedWorkflowStatusId?.toString() || ''}
                    onValueChange={(value) => setSelectedWorkflowStatusId(Number(value))}
                    disabled={formLocked || loadingWorkflowStatuses}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select Workflow status" />
                    </SelectTrigger>
                    <SelectContent className="bg-background z-50 max-h-64 overflow-y-auto">
                      {loadingWorkflowStatuses ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          Loading workflow statuses...
                        </div>
                      ) : workflowStatuses.length > 0 ? (
                        workflowStatuses.map((status) => (
                          <SelectItem key={status.workflow_status_id} value={status.workflow_status_id.toString()}>
                            {status.workflow_status}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          No workflow statuses available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                 {/* Appointment Scheduler */}
                 <AppointmentScheduler
                   key={`appointment-scheduler-${patientClinicDataId}-${nextPatientCounter}`}
                   clinicId={patientData?.clinic_id}
                   patientTimezone={patientTimezone}
                   disabled={formLocked}
                   onAppointmentChange={setAppointmentData}
                 />

                <div>
                  <Label className="text-sm font-semibold text-gray-700">
                    Comments <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="es-patient-comments"
                    name="patientComments"
                    data-testid="es-patient-comments-textarea"
                    value={comment}
                    onChange={e => {
                      if (e.target.value.length <= 500) {
                        handleCommentChange(e.target.value);
                      }
                    }}
                    placeholder="Enter your comments here..."
                    className="mt-1 min-h-[120px]"
                    maxLength={500}
                    disabled={formLocked}
                  />
                  <div className="text-right text-sm text-muted-foreground mt-1">
                    {comment.length}/500
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </div>

        {/* Transfer Acceptance Banner */}
        {(transferCompleted || transferState === 'accepted') && careSpecialist && (
          <div className="mt-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                <p className="text-green-800 font-medium">
                  Transfer accepted by: {careSpecialist}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons at Bottom */}
        <div className="mt-8 sticky bottom-0 bg-white">
          <div className="bg-white shadow-lg border-0 shadow-[0_0_15px_0px_rgba(0,0,0,0.1)] rounded-2xl p-4">
            <div className="flex justify-center space-x-4">
              {/* When coming from external source and save is completed, show only Back button */}
              {showBackButton && saveCompleted ? (
                <Button
                  onClick={onBack}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : formLocked ? (
                /* Show only Next Patient button when form is locked */
                <Button
                  id="es-next-patient-locked"
                  name="nextPatientLocked"
                  data-testid="es-next-patient-locked-button"
                  onClick={handleNextPatientWrapper}
                  disabled={isLoadingNext}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingNext ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Next Patient
                    </>
                  )}
                </Button>
              ) : (
                <>
                  {/* Show Back button when coming from external source */}
                  {showBackButton && onBack && (
                    <Button
                      onClick={onBack}
                      variant="outline"
                      className="px-8 py-3"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                  )}
                  
                  {!isPatientSaved && (
                    <Button
                      id="es-save-patient"
                      name="savePatient"
                      data-testid="es-save-patient-button"
                      onClick={handleSaveWrapper}
                      disabled={
                        isSaving || 
                        !comment.trim() || 
                        !selectedWorkflowStatusId ||
                        saveButtonDisabled
                      }
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  )}


                  {/* Next Patient Button - Hide when coming from external source */}
                  {!showBackButton && (
                    <Button
                      id="es-next-patient"
                      name="nextPatient"
                      data-testid="es-next-patient-button"
                      onClick={handleNextPatientWrapper}
                      disabled={hasUnsavedChanges || nextButtonDisabled || isLoadingNext}
                      variant="outline"
                      className={`px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed ${!(hasUnsavedChanges || nextButtonDisabled || isLoadingNext)
                        ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                        }`}
                    >
                      {isLoadingNext ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                          Loading...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          Next Patient
                        </>
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <div className="hbox-hx-sms-wrapper-area">
    <div className="hbox-sms-container" data-bs-theme="light"></div>
</div>

      {/* Transfer Status Modal */}
      {showTransferStatus && (
        <TransferStatus
          transferId={transferId}
          transferState={transferState}
          careSpecialist={careSpecialist}
          onClose={handleCloseTransferStatus}
        />
      )}

      {/* Group Completion Dialog */}
      <GroupCompletionDialog
        open={showGroupCompletionDialog}
        onConfirm={handleGroupCompletionConfirmWrapper}
        currentGroupName={currentGroupName}
        nextGroupName={nextGroupName}
      />
      

      {/* SMS Modal handled by hbox-sms package */}
    </div>
  );
};

export default PatientEnrollmentForm;